import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Star, Check, Loader2, Save } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'

/**
 * Admin → Website → Featured dishes.
 *
 * Writes the `storefront_featured_ids` setting (an array of product ids). The
 * storefront renders these as a "Featured" rail at the top of the menu. Curated
 * here rather than as a per-product flag so it lives entirely in the
 * online-ordering settings surface.
 */
const MAX_FEATURED = 8

export function AdminStorefrontFeatured() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const productsQuery = useQuery({
    queryKey: ['products', 'featured-picker'],
    // Pages rather than asking for 500 in one go — see F-BE-N60.
    queryFn: () => apiClient.getAllProducts({ available: true }),
  })

  const products = (productsQuery.data ?? []).filter((p) => p.is_available)
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    const saved = settingsQuery.data?.data?.storefront_featured_ids
    if (Array.isArray(saved)) setSelected(saved.filter((x): x is string => typeof x === 'string'))
  }, [settingsQuery.data])

  const toggle = (id: string) =>
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= MAX_FEATURED) {
        toast({ title: `You can feature up to ${MAX_FEATURED} items` })
        return prev
      }
      return [...prev, id]
    })

  const saveMutation = useMutation({
    mutationFn: () => apiClient.updateSetting('storefront_featured_ids', selected),
    onSuccess: () => {
      toast({ title: 'Featured items saved' })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) =>
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' }),
  })

  return (
    <Card className="space-y-5 p-5">
      <div className="flex items-center gap-3 border-b border-border/60 pb-3">
        <Star className="h-5 w-5 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Featured items</h3>
          <p className="text-xs text-muted-foreground">
            Highlight up to {MAX_FEATURED} items in a rail at the top of your storefront menu — your best-sellers or
            today's specials.
          </p>
        </div>
        <span className="text-xs font-medium text-muted-foreground">
          {selected.length}/{MAX_FEATURED}
        </span>
      </div>

      {productsQuery.isLoading ? (
        <div className="flex h-16 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {products.map((p) => {
            const on = selected.includes(p.id)
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                  on ? 'border-primary bg-primary/5' : 'border-border/60 hover:bg-muted/40'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
                  }`}
                >
                  {on && <Check className="h-3.5 w-3.5" />}
                </span>
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="h-8 w-8 rounded object-cover" />
                ) : (
                  <span className="h-8 w-8 rounded bg-muted" />
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.name}</span>
              </button>
            )
          })}
          {products.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No available products yet.</p>
          )}
        </div>
      )}

      <div className="flex justify-end border-t border-border/60 pt-4">
        <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="gap-1.5">
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save featured
        </Button>
      </div>
    </Card>
  )
}
